import { useEffect, useState } from "react";
import { useStore } from "@/stores/store-context";

const GUEST_ROLE = "workspace.guest";

export type MyJobRole = {
  /** Whether the Job role picker is on screen. The Preferences intro names the
   *  picker only while this is true, so keep the two reading this one value. */
  shown: boolean;
  workspaceSlug: string | undefined;
  jobRoleId: string | null;
  setJobRoleId: (id: string | null) => void;
};

/**
 * Loads the current member's job role and the active roles for the workspace in
 * the URL. Shown only inside a workspace, once the membership has loaded, and
 * never for guests, who cannot hold a job role. A membership that failed to load
 * keeps it hidden too: an empty picker would read as "no job role" whatever the
 * member holds, and could not save (NG-2642).
 */
export function useMyJobRole(): MyJobRole {
  const { router, workspaceJobRoles } = useStore();
  const workspaceSlug = router.workspaceSlug;

  const [jobRoleId, setJobRoleId] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const fetchJobRoles = workspaceJobRoles.fetchJobRoles;
  const service = workspaceJobRoles;

  useEffect(() => {
    if (!workspaceSlug) return;
    let cancelled = false;
    void (async () => {
      try {
        const [membership] = await Promise.all([
          service.fetchMyMembership(workspaceSlug),
          fetchJobRoles(workspaceSlug).catch((error: unknown) => {
            console.warn("[profile] job roles load failed", { workspaceSlug, error });
          }),
        ]);
        if (cancelled) return;
        setJobRoleId(membership.job_role_id ?? null);
        setRole(membership.role ?? null);
      } catch (error) {
        console.warn("Failed to load job-role preference", { workspaceSlug, error });
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceSlug, service, fetchJobRoles]);

  return {
    shown: Boolean(workspaceSlug) && loaded && role !== null && role !== GUEST_ROLE,
    workspaceSlug,
    jobRoleId,
    setJobRoleId,
  };
}
